
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Save, User, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { SecurePhotoUpload } from './SecurePhotoUpload';
import VenueSectionsDisplay from '@/components/venue/VenueSectionsDisplay';

const VenueProfileForm: React.FC = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  const [internalProfile, setInternalProfile] = useState(null);
  
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: '',
    last_name: '',
    username: '',
    phone: '',
    
    // Venue specific fields
    venue_name: '',
    venue_address: '',
    venue_description: '',
    venue_type: '',
    venue_capacity: '',
    facilities_available: [],
    hourly_rates: '',
    operating_hours: '',
    available_dates: '',
    booking_contact_email: '',
    venue_manager_name: '',
    venue_manager_contact: '',
    
    // Media galleries
    highlight_video_links: [] as string[],
    photo_gallery_links: [] as string[],
    
    // Amenities
    locker_rooms_available: false,
    parking_available: false,
    restrooms_available: false,
    concessions_available: false,
    
    // Profile Photo/Logo
    profile_photo: null as File | null,
    profile_photo_url: '',
    
    // Privacy settings
    show_profile_public: false,
    show_contact_public: false,
    show_venue_info_public: false,
    show_venue_rates_public: false,
  });

  // Load profile data on component mount
  useEffect(() => {
    const loadProfileData = async () => {
      if (internalUser) {
        // Load internal user profile
        try {
          const { data, error } = await supabase
            .from('internal_user_profiles')
            .select('*')
            .eq('user_id', internalUser.id)
            .maybeSingle();
          
          if (data) {
            setInternalProfile(data);
            setFormData({
              first_name: data.first_name || internalUser.username || '',
              last_name: data.last_name || '',
              username: data.username || internalUser.username || '',
              phone: data.phone || '',
              venue_name: data.venue_name || '',
              venue_address: data.venue_address || '',
              venue_description: (data as any).venue_description || '',
              venue_type: (data as any).venue_type || '',
              venue_capacity: (data as any).venue_capacity?.toString() || '',
              facilities_available: (data as any).facilities_available || [],
              hourly_rates: (data as any).hourly_rates?.toString() || '',
              operating_hours: (data as any).operating_hours || '',
              available_dates: (data as any).available_dates || '',
              booking_contact_email: (data as any).booking_contact_email || '',
              venue_manager_name: (data as any).venue_manager_name || '',
              venue_manager_contact: (data as any).venue_manager_contact || '',
              highlight_video_links: (data as any).highlight_video_links || [],
              photo_gallery_links: (data as any).photo_gallery_links || [],
              locker_rooms_available: (data as any).locker_rooms_available || false,
              parking_available: (data as any).parking_available || false,
              restrooms_available: (data as any).restrooms_available || false,
              concessions_available: (data as any).concessions_available || false,
              profile_photo: null,
              profile_photo_url: data.profile_photo_url || '',
              show_profile_public: data.show_profile_public || false,
              show_contact_public: data.show_contact_public || false,
              show_venue_info_public: (data as any).show_venue_info_public || false,
              show_venue_rates_public: (data as any).show_venue_rates_public || false,
            });
          }
        } catch (error) {
          console.error('Error loading internal profile:', error);
        }
      } else if (profile) {
        // Load Supabase profile
        setFormData({
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          username: profile.username || '',
          phone: profile.phone || '',
          venue_name: profile.venue_name || '',
          venue_address: profile.venue_address || '',
          venue_description: profile.venue_description || '',
          venue_type: profile.venue_type || '',
          venue_capacity: profile.venue_capacity?.toString() || '',
          facilities_available: profile.facilities_available || [],
          hourly_rates: profile.hourly_rates?.toString() || '',
          operating_hours: profile.operating_hours || '',
          available_dates: profile.available_dates || '',
          booking_contact_email: profile.booking_contact_email || '',
          venue_manager_name: profile.venue_manager_name || '',
          venue_manager_contact: profile.venue_manager_contact || '',
          highlight_video_links: profile.highlight_video_links || [],
          photo_gallery_links: profile.photo_gallery_links || [],
          locker_rooms_available: profile.locker_rooms_available || false,
          parking_available: profile.parking_available || false,
          restrooms_available: profile.restrooms_available || false,
          concessions_available: profile.concessions_available || false,
          profile_photo: null,
          profile_photo_url: profile.profile_photo_url || '',
          show_profile_public: profile.show_profile_public || false,
          show_contact_public: profile.show_contact_public || false,
          show_venue_info_public: profile.show_venue_info_public || false,
          show_venue_rates_public: profile.show_venue_rates_public || false,
        });
      }
    };

    loadProfileData();
  }, [supabaseUser, internalUser, profile]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  const handleArrayChange = (field: string, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter((item: string) => item !== value)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!supabaseUser?.id && !internalUser?.id) {
        throw new Error('No user found');
      }

      const userId = supabaseUser?.id || internalUser?.id;
      let photoUrl = formData.profile_photo_url;

      // Photo URL is already handled by SecurePhotoUpload component
      photoUrl = formData.profile_photo_url;

      // Prepare update data
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        username: formData.username,
        phone: formData.phone,
        venue_name: formData.venue_name,
        venue_address: formData.venue_address,
        venue_description: formData.venue_description,
        venue_type: formData.venue_type,
        venue_capacity: formData.venue_capacity ? parseInt(formData.venue_capacity) : null,
        facilities_available: formData.facilities_available,
        hourly_rates: formData.hourly_rates ? parseFloat(formData.hourly_rates) : null,
        operating_hours: formData.operating_hours,
        available_dates: formData.available_dates,
        booking_contact_email: formData.booking_contact_email,
        venue_manager_name: formData.venue_manager_name,
        venue_manager_contact: formData.venue_manager_contact,
        highlight_video_links: formData.highlight_video_links,
        photo_gallery_links: formData.photo_gallery_links,
        locker_rooms_available: formData.locker_rooms_available,
        parking_available: formData.parking_available,
        restrooms_available: formData.restrooms_available,
        concessions_available: formData.concessions_available,
        show_profile_public: formData.show_profile_public,
        show_contact_public: formData.show_contact_public,
        show_venue_info_public: formData.show_venue_info_public,
        show_venue_rates_public: formData.show_venue_rates_public,
        profile_photo_url: photoUrl,
        updated_at: new Date().toISOString()
      };

      // Update profile in Supabase
      if (supabaseUser) {
        const { error } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', userId);

        if (error) throw error;
      }

      // Update internal user profile if needed
      if (internalUser) {
        const { internalProfileService } = await import('@/services/internalProfileService');
        
        const internalUpdateData = {
          user_id: userId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          username: formData.username,
          phone: formData.phone,
          venue_name: formData.venue_name,
          venue_address: formData.venue_address,
          venue_description: formData.venue_description,
          venue_type: formData.venue_type,
          venue_capacity: formData.venue_capacity ? parseInt(formData.venue_capacity) : null,
          facilities_available: formData.facilities_available,
          hourly_rates: formData.hourly_rates ? parseFloat(formData.hourly_rates) : null,
          operating_hours: formData.operating_hours,
          available_dates: formData.available_dates,
          booking_contact_email: formData.booking_contact_email,
          venue_manager_name: formData.venue_manager_name,
          venue_manager_contact: formData.venue_manager_contact,
          highlight_video_links: formData.highlight_video_links,
          photo_gallery_links: formData.photo_gallery_links,
          locker_rooms_available: formData.locker_rooms_available,
          parking_available: formData.parking_available,
          restrooms_available: formData.restrooms_available,
          concessions_available: formData.concessions_available,
          show_profile_public: formData.show_profile_public,
          show_contact_public: formData.show_contact_public,
          show_venue_info_public: formData.show_venue_info_public,
          show_venue_rates_public: formData.show_venue_rates_public,
          profile_photo_url: photoUrl,
        };

        const result = await internalProfileService.upsertProfile(internalUpdateData);
        if (!result.success) {
          throw new Error(result.error || 'Failed to update internal profile');
        }
      }

      // Update local state
      setFormData(prev => ({ ...prev, profile_photo_url: photoUrl }));

      toast.success('Venue profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(`Failed to update profile: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const facilitiesOptions = [
    'Soccer Field', 'Basketball Court', 'Tennis Court', 'Swimming Pool',
    'Track & Field', 'Baseball Diamond', 'Gymnasium', 'Weight Room',
    'Training Room', 'Conference Room', 'Pro Shop', 'Medical Room'
  ];

  const venueTypes = [
    'Sports Complex', 'Stadium', 'Gymnasium', 'Field', 'Court', 
    'Pool', 'Track', 'Arena', 'Recreation Center'
  ];

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Venue Logo/Photo Section */}
        <SecurePhotoUpload
          currentPhotoPath={formData.profile_photo_url}
          userId={supabaseUser?.id || internalUser?.id || ''}
          fallbackInitials={(formData.first_name?.charAt(0) || '') + (formData.last_name?.charAt(0) || '')}
          onPhotoUpdated={(newPhotoPath) => handleInputChange('profile_photo_url', newPhotoPath)}
          disabled={loading}
        />

        {/* Basic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-gray-300 flex items-center gap-2">
                  First Name <span className="text-red-400 text-xs">* Required</span>
                </Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white focus:border-sports-green"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-gray-300 flex items-center gap-2">
                  Last Name <span className="text-red-400 text-xs">* Required</span>
                </Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white focus:border-sports-green"
                  required
                />
              </div>
              <div>
                <Label htmlFor="username" className="text-gray-300 flex items-center gap-2">
                  Username <span className="text-red-400 text-xs">* Required</span>
                </Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white focus:border-sports-green"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Venue Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Venue Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="venue_name" className="text-gray-300 flex items-center gap-2">
                Venue Name <span className="text-red-400 text-xs">* Required</span>
              </Label>
              <Input
                id="venue_name"
                value={formData.venue_name}
                onChange={(e) => handleInputChange('venue_name', e.target.value)}
                className="bg-gray-700 border-gray-600 text-white focus:border-sports-green"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="venue_address" className="text-gray-300">Venue Address</Label>
              <Textarea
                id="venue_address"
                value={formData.venue_address}
                onChange={(e) => handleInputChange('venue_address', e.target.value)}
                placeholder="Full address of the venue..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="venue_description" className="text-gray-300">Venue Description</Label>
              <Textarea
                id="venue_description"
                value={formData.venue_description}
                onChange={(e) => handleInputChange('venue_description', e.target.value)}
                placeholder="Describe your venue..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="venue_type" className="text-gray-300">Venue Type</Label>
                <Input
                  id="venue_type"
                  value={formData.venue_type}
                  onChange={(e) => handleInputChange('venue_type', e.target.value)}
                  placeholder="e.g., Sports Complex, Stadium"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="venue_capacity" className="text-gray-300">Capacity</Label>
                <Input
                  id="venue_capacity"
                  type="number"
                  value={formData.venue_capacity}
                  onChange={(e) => handleInputChange('venue_capacity', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Facilities Available */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Facilities Available</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {facilitiesOptions.map((facility) => (
                <div key={facility} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={facility}
                    checked={formData.facilities_available.includes(facility)}
                    onChange={(e) => handleArrayChange('facilities_available', facility, e.target.checked)}
                    className="w-4 h-4 text-sports-green bg-gray-700 border-gray-600 rounded focus:ring-sports-green"
                  />
                  <Label htmlFor={facility} className="text-gray-300 text-sm cursor-pointer">
                    {facility}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Amenities */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Amenities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                <Label className="text-white">Locker Rooms</Label>
                <Switch
                  checked={formData.locker_rooms_available}
                  onCheckedChange={(checked) => handleInputChange('locker_rooms_available', checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                <Label className="text-white">Parking</Label>
                <Switch
                  checked={formData.parking_available}
                  onCheckedChange={(checked) => handleInputChange('parking_available', checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                <Label className="text-white">Restrooms</Label>
                <Switch
                  checked={formData.restrooms_available}
                  onCheckedChange={(checked) => handleInputChange('restrooms_available', checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                <Label className="text-white">Concessions</Label>
                <Switch
                  checked={formData.concessions_available}
                  onCheckedChange={(checked) => handleInputChange('concessions_available', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Booking Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Booking Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="hourly_rates" className="text-gray-300">Hourly Rates</Label>
              <Input
                id="hourly_rates"
                value={formData.hourly_rates}
                onChange={(e) => handleInputChange('hourly_rates', e.target.value)}
                placeholder="e.g., $50-100/hour"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="operating_hours" className="text-gray-300">Operating Hours</Label>
              <Textarea
                id="operating_hours"
                value={formData.operating_hours}
                onChange={(e) => handleInputChange('operating_hours', e.target.value)}
                placeholder="e.g., Mon-Fri: 6AM-10PM, Sat-Sun: 8AM-8PM"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="available_dates" className="text-gray-300">Available Dates</Label>
              <Textarea
                id="available_dates"
                value={formData.available_dates}
                onChange={(e) => handleInputChange('available_dates', e.target.value)}
                placeholder="Describe your availability..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="booking_contact_email" className="text-gray-300">Booking Contact Email</Label>
                <Input
                  id="booking_contact_email"
                  type="email"
                  value={formData.booking_contact_email}
                  onChange={(e) => handleInputChange('booking_contact_email', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="venue_manager_contact" className="text-gray-300">Manager Contact</Label>
                <Input
                  id="venue_manager_contact"
                  value={formData.venue_manager_contact}
                  onChange={(e) => handleInputChange('venue_manager_contact', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="venue_manager_name" className="text-gray-300">Venue Manager Name</Label>
              <Input
                id="venue_manager_name"
                value={formData.venue_manager_name}
                onChange={(e) => handleInputChange('venue_manager_name', e.target.value)}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Video Gallery */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Video Gallery</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white">Venue Video Links</Label>
              <p className="text-sm text-gray-400 mb-2">Add YouTube or Vimeo links showcasing your venue</p>
              {(formData.highlight_video_links || []).map((video, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input
                    value={video}
                    onChange={(e) => {
                      const newVideos = [...(formData.highlight_video_links || [])];
                      newVideos[index] = e.target.value;
                      setFormData(prev => ({ ...prev, highlight_video_links: newVideos }));
                    }}
                    placeholder="https://youtube.com/watch?v=..."
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newVideos = (formData.highlight_video_links || []).filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, highlight_video_links: newVideos }));
                    }}
                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const newVideos = [...(formData.highlight_video_links || []), ''];
                  setFormData(prev => ({ ...prev, highlight_video_links: newVideos }));
                }}
                className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
              >
                Add Video Link
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Photo Gallery */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Photo Gallery</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white">Venue Photo Links</Label>
              <p className="text-sm text-gray-400 mb-2">Add direct links to venue photos</p>
              {(formData.photo_gallery_links || []).map((photo, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input
                    value={photo}
                    onChange={(e) => {
                      const newPhotos = [...(formData.photo_gallery_links || [])];
                      newPhotos[index] = e.target.value;
                      setFormData(prev => ({ ...prev, photo_gallery_links: newPhotos }));
                    }}
                    placeholder="https://example.com/venue-photo.jpg"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newPhotos = (formData.photo_gallery_links || []).filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, photo_gallery_links: newPhotos }));
                    }}
                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPhotos = [...(formData.photo_gallery_links || []), ''];
                  setFormData(prev => ({ ...prev, photo_gallery_links: newPhotos }));
                }}
                className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
              >
                Add Photo Link
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Privacy Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Make Profile Public</Label>
                <p className="text-sm text-gray-400">Allow others to view your venue</p>
              </div>
              <Switch
                checked={formData.show_profile_public}
                onCheckedChange={(checked) => handleInputChange('show_profile_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Contact Information</Label>
                <p className="text-sm text-gray-400">Display contact info publicly</p>
              </div>
              <Switch
                checked={formData.show_contact_public}
                onCheckedChange={(checked) => handleInputChange('show_contact_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Venue Information</Label>
                <p className="text-sm text-gray-400">Display venue details publicly</p>
              </div>
              <Switch
                checked={formData.show_venue_info_public}
                onCheckedChange={(checked) => handleInputChange('show_venue_info_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Rates</Label>
                <p className="text-sm text-gray-400">Display pricing publicly</p>
              </div>
              <Switch
                checked={formData.show_venue_rates_public}
                onCheckedChange={(checked) => handleInputChange('show_venue_rates_public', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="bg-sports-green text-black hover:bg-green-600"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Venue Profile
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Venue Sections Display - Only show after venue is saved */}
      {(formData.venue_name && (supabaseUser?.id || internalUser?.id)) && (
        <div className="mt-8">
          <VenueSectionsDisplay 
            venueId={supabaseUser?.id || internalUser?.id || ''} 
            showAddButton={true}
            onAddSection={() => {
              toast.info('Section management feature coming soon!');
            }}
          />
        </div>
      )}
    </div>
  );
};

export default VenueProfileForm;
